import { useState } from 'react'

import { Card } from '@/components/ui'
import { numberToCurrency as money } from '@/utils'
import { AccountHeading } from './detail/AccountHeading'
import type { AccountSection } from './detail/detailScope'
import { gapSentence } from './detail/gapSentence'
import { GROUP_DIVIDER, GROUPS_SHOWN } from './detail/groupLayout'
import { PageLink } from './detail/PageLink'
import { ShowAllButton } from './detail/ShowAllButton'
import type { YearToDateRow } from './types'

interface YearToDateProps {
  sections: AccountSection[]
  showNames: boolean
  year: string
}

const Figures = ({ figures }: { figures: YearToDateRow[] }) => (
  <dl className="mt-4 grid grid-cols-2 gap-6 md:grid-cols-4">
    {figures.map((figure) => (
      <div key={figure.id} className="min-w-0">
        <dt className="truncate text-xs text-ink-subtle">{figure.label}</dt>
        <dd className="mt-0.5 flex items-baseline gap-2 tabular-nums">
          {money(figure.amount)}
          <PageLink
            fileUrl={figure.statementFileUrl}
            page={figure.sourcePage}
          />
        </dd>
      </div>
    ))}
  </dl>
)

// An account with no statement this month still gets its line, so a gap is said, not skipped.
const groupsOf = (sections: AccountSection[]) => {
  const withFigures = sections.filter(
    (section) => section.yearToDate.length > 0,
  )
  if (withFigures.length === 0) return []
  return sections.filter(
    (section) =>
      section.yearToDate.length > 0 || section.account.status !== 'statement',
  )
}

// The year to date figures each statement printed, one group per account.
export const YearToDate = ({ sections, showNames, year }: YearToDateProps) => {
  const [showAll, setShowAll] = useState(false)
  const groups = groupsOf(sections)
  if (groups.length === 0) return null
  const shown = showAll ? groups : groups.slice(0, GROUPS_SHOWN)
  return (
    <Card className="p-6 text-sm">
      <h3 className="text-base font-semibold">Year to date, {year}</h3>
      {shown.map((section, position) => (
        <div
          key={section.account.accountId}
          className={position === 0 ? 'mt-5' : GROUP_DIVIDER}
        >
          <AccountHeading
            section={section}
            showName={showNames}
            gapSentence={gapSentence(section.account)}
          />
          {section.yearToDate.length > 0 && (
            <Figures figures={section.yearToDate} />
          )}
        </div>
      ))}
      {!showAll && groups.length > GROUPS_SHOWN && (
        <div className="mt-6">
          <ShowAllButton
            count={groups.length}
            noun="accounts with year to date figures"
            onClick={() => setShowAll(true)}
          />
        </div>
      )}
    </Card>
  )
}
